import './styles.scss'

import { Box, Grid, IconButton, Typography } from '@mui/material'
import Analytics, { Events } from 'analytics/Analytics'
import { ArrowRight, logo } from 'assets/icons'
import { AxiosError } from 'axios'
import Calendar from 'components/calendar/Calendar'
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog'
import EmailHighlight from 'components/dialogs/embed/EmailHighlight'
import { processLogout } from 'components/providers/AuthProvider'
import { useCalendar } from 'components/providers/CalendarProvider'
import { doGetChanges } from 'components/providers/GetChangesProvider'
import dayjs from 'dayjs'
import i18n from 'i18n'
import { t } from 'i18next'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getEventsForGroup } from 'services/api/event'
import Database from 'services/db/Database'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import { theme } from 'theme'
import { GroupDoc } from 'types/api/changes'
import {
  CalendarRange,
  getCalendarRangeByString
} from 'types/components/calendar'
import { LocalStorageKey } from 'types/services/localStorage'
import useFitText from 'use-fit-text'
import { APP_LINK, isMobileDevice } from 'utils/constants'
import { extractDeepLinkCode } from 'utils/deeplink'
import { appStoreLinkGroupcal, getMobileOS } from 'utils/device'
import { isTestEnv } from 'utils/env'
import GuestHeader from './GuestHeader'

export interface GuestModeProps {
  showHeader?: string
  defaultView?: CalendarRange
  groupId: string
  width?: string
  height?: string
  preview?: boolean
  groupsIds?: string[]
  calendarTitle?: string
}

export default function GuestMode(props: GuestModeProps) {
  const calendar = useCalendar()

  const [group, onGroup] = useState<GroupDoc | undefined>(undefined)
  const [showNotValidCalendar, setShowNotValidCalendar] = useState(false)

  useEffect(() => {
    if (group) {
      const inviteCode = extractDeepLinkCode(group?.PrivateLinkUrl ?? '') // set this dynamically
      if (getMobileOS() === 'ios') {
        if (inviteCode) {
          // Replace 'yourappscheme://' with your actual URL scheme
          const schemeUrl = `GroupCal24me://invite_code=${inviteCode}`
          const iframe = document.createElement('iframe')
          iframe.style.display = 'none'
          iframe.src = schemeUrl
          document.body.appendChild(iframe)

          // Optionally, fallback to App Store after a timeout
          setTimeout(() => {
            document.body.removeChild(iframe)
          }, 2000)
        } else {
        }
      }
    }
  }, [group])

  const isEmbed = props.preview

  useEffect(() => {
    Analytics.getInstance().sendEvent(Events.GEUST_MODE_OPEN)
  }, [])

  const queryParams = new URLSearchParams(location.search)
  const showHeader = queryParams.get('showHeader')
  const isPreview = queryParams.get('preview') === '1'
  const hidePoweredBy = queryParams.get('removeBranding') === '1'
  const [canHideBranding, setCanHideBranding] = useState(false)

  useEffect(() => {
    calendar.onView(
      getCalendarRangeByString(
        LocalStorage.get(LocalStorageKey.CALENDAR_VIEW) ?? ''
      ) ?? CalendarRange.DAY
    )
  }, [])

  useEffect(() => {
    getEventsForGroup(
      props.groupId,
      dayjs().unix(),
      dayjs().unix(),
      props.groupsIds
    )
      .then((eventsAndGroupData) => {
        if (!isPreview) onGroup(eventsAndGroupData.groups[0])
        return eventsAndGroupData
      })
      .then((eventsAndGroupData) => {
        setCanHideBranding(eventsAndGroupData.canRemovePoweredBy)
      })
      .catch((error) => {
        if (error instanceof AxiosError) {
          const httpCode = error.response?.status

          if (httpCode === 403) {
            const code = (error.response?.data as unknown as any)['code']
            /**
             * we have specific cases when server return 403, but we should not retry the request, such as:
             * out of tier, user blocked from adding/editing events etc
             *
             */

            if (code === -113) {
              setShowNotValidCalendar(true)
            }
          }
        }
      })
  }, [])

  return (
    <Box
      bgcolor="transparent"
      display="flex"
      className="guest-root"
      position={'relative'}
      height={props.height ? props.height : '100dvh'}
      width={props.width ? props.width : '100dvw'}
    >
      <Box
        bgcolor={isEmbed || props.preview ? 'transparent' : '#e5E5E5'}
        display="flex"
        flex={1}
        padding={isMobileDevice() ? '2px 16px 16px 16px' : 4}
        minWidth={'100px'}
        minHeight={'100px'}
        flexDirection={{ xs: 'column', sm: 'column' }}
      >
        {(showHeader ?? props.showHeader) === '0' ? (
          <></>
        ) : (
          <GuestHeader group={group} />
        )}
        <Box height={'100%'}>
          <Calendar
            calendarTitle={props.calendarTitle}
            showOnlySelectedGroup
            groupId={props.groupId}
            groupIds={props.groupsIds}
            readOnly
            embedMode={props.preview}
          />
        </Box>
      </Box>
      <Box
        id={'groupcal-branding-button'}
        position={'absolute'}
        zIndex={99999}
        display={canHideBranding && hidePoweredBy ? 'none' : 'flex'}
        bottom={32}
        right={i18n.dir() === 'ltr' ? 32 : undefined}
        left={i18n.dir() === 'rtl' ? 32 : undefined}
        sx={{
          borderRadius: '4px',
          backgroundColor: '#ffffffb0',
          boxShadow: '0px 1px 8px rgba(0, 0, 0, 0.1)',
          border: '1px solid #eeeeee'
        }}
      >
        <Grid
          item
          wrap={'nowrap'}
          direction={'row'}
          display={'flex'}
          padding={theme.spacing(0.5, 2)}
          alignItems={'center'}
          gap={1}
        >
          <IconButton onClick={() => {}} sx={{ padding: 0 }}>
            <img
              src={logo}
              style={{ width: 18, height: 18, cursor: 'default' }}
            />
          </IconButton>
          <span onClick={() => {}} style={{ cursor: 'default' }}>
            <Typography
              fontFamily={'NotoSans'}
              fontSize={'14px'}
              fontWeight={400}
            >
              GroupCal
            </Typography>
          </span>
        </Grid>
      </Box>
      {showNotValidCalendar && (
        <ConfirmationDialog
          leftButtonHidden
          handleRightButton={() => {
            setShowNotValidCalendar(false)
          }}
          title={t('notValidCalendar')}
          content={<EmailHighlight text={t('notValidCalendarDesc')} />}
          open
        />
      )}
    </Box>
  )
}
export interface JoinGroupButtonProps {
  title?: string
  group?: GroupDoc
}

export function JoinGroupButton(props: JoinGroupButtonProps) {
  const { fontSize, ref } = useFitText({
    minFontSize: 50,
    maxFontSize: 100
  })
  const navigate = useNavigate()
  const goToLogin = () => {
    Analytics.getInstance().sendEvent(Events.GEUST_MODE_OPEN_FULL_VERSION)
    const inviteCode = extractDeepLinkCode(props.group?.PrivateLinkUrl ?? '') // set this dynamically

    Database.clear()
      .then(() => {
        if (getMobileOS() === 'android') {
          if (inviteCode) {
            const playStoreUrl = `https://play.google.com/store/apps/details?id=app.groupcal.www${isTestEnv() ? '.debug' : ''}&referrer=invite_code%3D${encodeURIComponent(inviteCode)}`
            window.open(playStoreUrl, '_self')
          } else {
            // Open new page in the same tab with mobile link
            window.location.href = `${APP_LINK}/${LocalStorage.get(
              LocalStorageKey.MOBILE_LINK
            )}`
          }
        } else if (getMobileOS() === 'ios') {
          navigator.clipboard.writeText(`Deeplink=${inviteCode}`)
          window.open(appStoreLinkGroupcal, '_blank')

          if (!inviteCode) {
            window.location.href = `${APP_LINK}/${LocalStorage.get(
              LocalStorageKey.MOBILE_LINK
            )}`
          }
        } else {
          navigate('/')
          LocalStorage.remove(LocalStorageKey.LAST_UPDATE)
          LocalStorage.remove(LocalStorageKey.CLEAN_GET_CHANGES_DONE)
          processLogout().then(() => {
            doGetChanges(null, undefined, null)
          })
        }
      })
      .catch((error) => {
        Logger.red('GuestMode', 'Error clearing database', error)
      })
  }

  if (isMobileDevice()) {
    return (
      <div
        onClick={goToLogin}
        id="joinGroupButton"
        style={{
          flexShrink: 0, // never shrink
          flexGrow: 0, // only take needed space
          backgroundColor: '#ffffff',
          border: '1px solid #9b9b9b',
          borderRadius: '68px',
          padding: '8px 8px',
          height: '40px'
        }}
      >
        {t('joinCalendarTitle')}
      </div>
    )
  }

  return (
    <div
      ref={ref}
      style={{
        height: isMobileDevice() ? '30px' : '50px',
        fontSize: fontSize,
        width: '100%'
      }}
      className="goToSignUp"
      onClick={goToLogin}
      id="joinGroupButton"
    >
      {props.title ?? t('joinCalnedar')}
      <ArrowRight className="arrow" style={{ marginLeft: 8 }} />
    </div>
  )
}
